import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  FormGroup,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab
} from '@mui/material';
import ErpDataGrid from '../../components/common/ErpDataGrid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  PlayArrow as StartIcon,
  Pause as SuspendIcon,
  HealthAndSafety as HealthIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  HourglassTop as InProgressIcon,
  Close as CloseIcon,
  Verified as VerifiedIcon,
  ThumbUp as ApproveIcon,
  RocketLaunch as ProvisionIcon,
  Info as InfoIcon,
  AccountTree as TreeIcon,
  NoteAdd as QuickAddIcon,
  Person as PersonIcon,
  HowToReg as HowToRegIcon,
  TableRows as TableIcon,
  Lock as LockIcon,
  Dns as DnsIcon,
  ViewAgenda as ListLayoutIcon,
  ViewWeek as HorizontalTabsIcon,
  ViewSidebar as VerticalTabsIcon,
  Apps as AllIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth, usePermissions } from '../../context/AuthContext';
import TenantDestroyPlanPanel from '../../components/TenantDestroyPlanPanel';
import TenantDestroyReportDialog from '../../components/TenantDestroyReportDialog';
import TenantDestroyProgressDialog from '../../components/TenantDestroyProgressDialog';
import TenantsCompanyTree, { openPopup } from './TenantsCompanyTree';
import TenantViewDialog from './TenantViewDialog';
import { TENANT_ENVIRONMENTS } from '../../constants/environments';

const isBaseTenant = (row) => Boolean(row?.is_base);

/** Canonical service codes shown in Version column + legend. */
const SERVICE_LEGEND = [
  { code: 'FE', label: 'Frontend web UI' },
  { code: 'BE', label: 'Backend API' },
  { code: 'DB', label: 'PostgreSQL database' },
  { code: 'JITSI', label: 'Jitsi video stack (web / jvb / jicofo / prosody / jibri)' },
  { code: 'CUST', label: 'Customization API' },
  { code: 'EMAIL', label: 'Email extractor service' },
  { code: 'VIDEO', label: 'Video creator service' },
  { code: 'BKUP', label: 'Database backup job' }
];

const STATUS_LEGEND_BASE = [
  { code: 'Up', label: 'Service replica ready', color: 'success' },
  { code: 'Down', label: 'Service not ready', color: 'error' }
];

const STATUS_LEGEND_HUB = [
  { code: 'Current', label: 'Matches base tenant version', color: 'success' },
  { code: 'Behind', label: 'Older than base — consider upgrading', color: 'error' },
  { code: 'Ahead', label: 'Newer than recorded base', color: 'info' }
];

const HUB_ROLE_ORDER = ['frontend', 'backend', 'database'];
const ROLE_SHORT = { frontend: 'FE', backend: 'BE', database: 'DB' };

const LIST_LAYOUT_STORAGE_KEY = 'tenanthub.tenants.listLayout';
const SEGMENT_STORAGE_KEY = 'tenanthub.tenants.segment';

const readStored = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v || fallback;
  } catch {
    return fallback;
  }
};

const TENANT_SEGMENTS = [
  { value: 'base', label: 'Base Tenant', icon: <LockIcon fontSize="small" /> },
  { value: 'provisioned', label: 'Provisioned Tenants', icon: <DnsIcon fontSize="small" /> },
  { value: 'all', label: 'All Tenants', icon: <AllIcon fontSize="small" /> }
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All status', icon: <FilterIcon fontSize="small" />, color: 'default' },
  { value: 'submitted', label: 'Submitted', icon: <PendingIcon fontSize="small" />, color: 'warning' },
  { value: 'client_approved', label: 'Client approved', icon: <PersonIcon fontSize="small" />, color: 'info' },
  { value: 'erp_verified', label: 'ERP verified', icon: <HowToRegIcon fontSize="small" />, color: 'info' },
  { value: 'approved', label: 'Approved', icon: <ApproveIcon fontSize="small" />, color: 'success' },
  { value: 'in_progress', label: 'In progress', icon: <InProgressIcon fontSize="small" />, color: 'warning' },
  { value: 'active', label: 'Active', icon: <CheckIcon fontSize="small" />, color: 'success' },
  { value: 'suspended', label: 'Suspended', icon: <SuspendIcon fontSize="small" />, color: 'default' },
  { value: 'destroying', label: 'Destroying', icon: <InProgressIcon fontSize="small" />, color: 'error', superAdminOnly: true },
  { value: 'destroyed', label: 'Deleted', icon: <DeleteIcon fontSize="small" />, color: 'error', superAdminOnly: true }
];


const mapServiceToCode = (svc) => {
  const name = String(svc?.name || '').toLowerCase();
  const role = String(svc?.role || '').toLowerCase();
  const repo = String(svc?.repository || svc?.image || '').toLowerCase();
  const blob = `${name} ${role} ${repo}`;
  if (blob.includes('frontend') || role === 'frontend') return 'FE';
  if (blob.includes('backend') || role === 'backend') return 'BE';
  if (blob.includes('postgres') || (role === 'database' && blob.includes('postgres'))) return 'DB';
  if (blob.includes('jitsi') || blob.includes('jibri') || blob.includes('jicofo') || blob.includes('jvb') || blob.includes('prosody')) {
    return 'JITSI';
  }
  if (blob.includes('customization')) return 'CUST';
  if (blob.includes('email')) return 'EMAIL';
  if (blob.includes('video-creator') || blob.includes('video_creator')) return 'VIDEO';
  if (blob.includes('database-backup') || blob.includes('backup')) return 'BKUP';
  if (role === 'database') return 'DB';
  return null;
};

const BASE_CODE_ORDER = ['FE', 'BE', 'DB', 'JITSI', 'CUST', 'EMAIL', 'VIDEO', 'BKUP'];

const lagChipProps = (lag) => {
  if (lag === 'behind') return { label: 'Behind', color: 'error' };
  if (lag === 'current') return { label: 'Current', color: 'success' };
  if (lag === 'ahead') return { label: 'Ahead', color: 'info' };
  return { label: '—', color: 'default' };
};

/** Compact 3-column grid: code | version | status */
const ServiceVersionCell = ({ row, mode = 'hub' }) => {
  const compare = Array.isArray(row?.version_compare?.components) ? row.version_compare.components : [];
  const services = Array.isArray(row?.runtime_services?.services) ? row.runtime_services.services : [];
  const byCode = new Map();

  if (mode === 'hub') {
    compare.forEach((c) => {
      if (!c?.role || !HUB_ROLE_ORDER.includes(c.role)) return;
      const code = ROLE_SHORT[c.role];
      byCode.set(code, {
        code,
        version: c.tenant_version || null,
        base: c.base_version || null,
        lag: c.lag || null,
        up: null
      });
    });
    services.forEach((s) => {
      const code = mapServiceToCode(s);
      if (!code || !['FE', 'BE', 'DB'].includes(code)) return;
      const prev = byCode.get(code) || { code, version: null, base: null, lag: null, up: null };
      byCode.set(code, {
        ...prev,
        version: prev.version || s.tag || null,
        up: typeof s.up === 'boolean' ? s.up : prev.up
      });
    });
  } else {
    // Base table: include FE/BE/DB/JITSI/etc from live K8s snapshot
    services.forEach((s) => {
      const code = mapServiceToCode(s);
      if (!code) return;
      const prev = byCode.get(code);
      // Prefer postgresql image for DB; prefer jitsi-web tag for JITSI
      const name = String(s.name || '').toLowerCase();
      const prefer = !prev
        || (code === 'DB' && name.includes('postgres'))
        || (code === 'JITSI' && name.includes('jitsi-web'))
        || (code === 'FE' && name.includes('frontend'))
        || (code === 'BE' && name.includes('backend'));
      if (!prefer && prev) {
        // Still OR readiness if any replica of this family is up
        if (s.up) prev.up = true;
        return;
      }
      byCode.set(code, {
        code,
        version: s.tag || prev?.version || null,
        base: null,
        lag: null,
        up: typeof s.up === 'boolean' ? s.up : (prev?.up ?? null)
      });
    });
    compare.forEach((c) => {
      if (!c?.role || !HUB_ROLE_ORDER.includes(c.role)) return;
      const code = ROLE_SHORT[c.role];
      const prev = byCode.get(code) || { code, version: null, base: null, lag: null, up: null };
      byCode.set(code, {
        ...prev,
        version: prev.version || c.tenant_version || c.base_version || null
      });
    });
  }

  const order = mode === 'base' ? BASE_CODE_ORDER : ['FE', 'BE', 'DB'];
  const rows = order.map((code) => byCode.get(code)).filter(Boolean);
  if (!rows.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
        —
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '44px minmax(0, 1fr) 72px',
        columnGap: 1,
        rowGap: 0.35,
        width: '100%',
        maxWidth: '100%',
        alignItems: 'center',
        py: 0.35,
        boxSizing: 'border-box'
      }}
    >
      {rows.map((r) => {
        const tip = [
          r.code,
          r.version || 'no version',
          r.base ? `base ${r.base}` : null,
          r.lag || null,
          r.up === true ? 'up' : r.up === false ? 'down' : null
        ].filter(Boolean).join(' · ');
        const status = mode === 'base'
          ? (r.up === true
            ? { label: 'Up', color: 'success' }
            : r.up === false
              ? { label: 'Down', color: 'error' }
              : { label: '—', color: 'default' })
          : lagChipProps(r.lag);
        return (
          <React.Fragment key={r.code}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'text.secondary', lineHeight: 1.2 }}
            >
              {r.code}
            </Typography>
            <Tooltip title={tip} placement="top" enterDelay={400}>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {r.version || '—'}
              </Typography>
            </Tooltip>
            <Chip
              size="small"
              label={status.label}
              color={status.color}
              variant={status.label === '—' ? 'outlined' : 'filled'}
              sx={{
                height: 20,
                maxWidth: 72,
                fontSize: '0.65rem',
                fontWeight: 600,
                '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' }
              }}
            />
          </React.Fragment>
        );
      })}
    </Box>
  );
};

const TenantTableLegend = ({ mode = 'hub' }) => {
  const statusItems = mode === 'base' ? STATUS_LEGEND_BASE : STATUS_LEGEND_HUB;
  const serviceItems = mode === 'base'
    ? SERVICE_LEGEND
    : SERVICE_LEGEND.filter((s) => ['FE', 'BE', 'DB'].includes(s.code));
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: mode === 'base' ? 'rgba(198, 40, 40, 0.04)' : 'grey.50'
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
        Legend
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
        {serviceItems.map((item) => (
          <Typography key={item.code} variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{item.code}</Box>
            {' — '}{item.label}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {statusItems.map((item) => (
          <Box key={item.code} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip size="small" label={item.code} color={item.color} sx={{ height: 20, fontSize: '0.65rem' }} />
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const TenantsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [baseTenants, setBaseTenants] = useState([]);
  const [baseLoading, setBaseLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  
  // Dialogs
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [destroyPreview, setDestroyPreview] = useState(null);
  const [destroyPreviewLoading, setDestroyPreviewLoading] = useState(false);
  const [deleteBackup, setDeleteBackup] = useState(true);
  const [deleteDestroyInfra, setDeleteDestroyInfra] = useState(true);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [destroyReport, setDestroyReport] = useState(null);
  const [destroyReportOpen, setDestroyReportOpen] = useState(false);
  const [destroyProgressOpen, setDestroyProgressOpen] = useState(false);
  const [destroyProgressTenant, setDestroyProgressTenant] = useState(null);
  const [destroyProgressData, setDestroyProgressData] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [sendForApprovalDialog, setSendForApprovalDialog] = useState(false);
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [clientBehalfDialog, setClientBehalfDialog] = useState(false);
  const [clientBehalfReason, setClientBehalfReason] = useState('');
  const [clientBehalfLoading, setClientBehalfLoading] = useState(false);
  
  // Provisioning Progress Dialog
  const [progressDialog, setProgressDialog] = useState(false);
  const [progressTenant, setProgressTenant] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [addTenantChooserOpen, setAddTenantChooserOpen] = useState(false);
  const [viewMode, setViewMode] = useState(searchParams.get('view') === 'tree' ? 'tree' : 'table');
  const [listLayout, setListLayout] = useState(() => {
    const fromUrl = searchParams.get('layout');
    if (fromUrl === 'list' || fromUrl === 'horizontal' || fromUrl === 'vertical') return fromUrl;
    const stored = readStored(LIST_LAYOUT_STORAGE_KEY, 'list');
    return ['list', 'horizontal', 'vertical'].includes(stored) ? stored : 'list';
  });
  const [tenantSegment, setTenantSegment] = useState(() => {
    const fromUrl = searchParams.get('segment');
    if (fromUrl === 'base' || fromUrl === 'provisioned' || fromUrl === 'all') return fromUrl;
    const stored = readStored(SEGMENT_STORAGE_KEY, 'all');
    return ['base', 'provisioned', 'all'].includes(stored) ? stored : 'all';
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTenantId, setViewTenantId] = useState(null);
  const [baseRegisterLoading, setBaseRegisterLoading] = useState(false);
  const [baseProtectedDialog, setBaseProtectedDialog] = useState(false);
  const [baseProtectedTenant, setBaseProtectedTenant] = useState(null);

  const openTenantDetail = (tenantOrId) => {
    const id = typeof tenantOrId === 'string' ? tenantOrId : tenantOrId?.id;
    if (!id) return;
    navigate(`/tenants/${id}`);
  };

  const openTenantView = (tenantOrId) => {
    const id = typeof tenantOrId === 'string' ? tenantOrId : tenantOrId?.id;
    if (!id) return;
    setViewTenantId(id);
    setViewDialogOpen(true);
  };

  const closeTenantView = () => {
    setViewDialogOpen(false);
    setViewTenantId(null);
  };

  useEffect(() => {
    fetchBaseTenants();
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [page, pageSize, search, status, listLayout, tenantSegment]);

  useEffect(() => {
    try {
      localStorage.setItem(LIST_LAYOUT_STORAGE_KEY, listLayout);
      localStorage.setItem(SEGMENT_STORAGE_KEY, tenantSegment);
    } catch {
      /* ignore */
    }
  }, [listLayout, tenantSegment]);

  // Auto-refresh progress when dialog is open
  useEffect(() => {
    let intervalId;
    if (progressDialog && progressTenant && progressTenant.provisioning_status === 'in_progress') {
      intervalId = setInterval(() => {
        fetchProgress(progressTenant.id, false);
      }, 2000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [progressDialog, progressTenant?.id, progressTenant?.provisioning_status]);

  useEffect(() => {
    if (!destroyProgressOpen || !destroyProgressTenant?.id) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/tenants/${destroyProgressTenant.id}/destroy-progress`);
        if (!cancelled) {
          setDestroyProgressData(res.data?.data || null);
          const st = res.data?.data?.status;
          if (st === 'deleted' || st === 'destroy_failed') {
            fetchTenants();
          }
        }
      } catch {
        /* ignore */
      }
    };
    poll();
    const intervalId = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [destroyProgressOpen, destroyProgressTenant?.id]);

  const isDeletedRow = (row) => row?.status === 'deleted' || Boolean(row?.deleted_at);
  const isDestroyLifecycle = (row) => ['destroying', 'deleted', 'destroy_failed'].includes(row?.status) || Boolean(row?.deleted_at);

  const showBaseProtected = (tenant) => {
    setBaseProtectedTenant(tenant || selectedTenant);
    setBaseProtectedDialog(true);
  };

  const registerBaseTenants = async () => {
    setBaseRegisterLoading(true);
    try {
      const res = await api.post('/tenants/base/register');
      const data = res.data?.data;
      toast.success(res.data?.message || 'Base tenants registered');
      if (data?.registered?.length) {
        const summary = data.registered
          .map((r) => `${r.tenant_name}${r.discovered ? ' (found)' : ' (expected)'}`)
          .join(', ');
        toast.info(summary);
      }
      fetchBaseTenants();
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to register base tenants');
    } finally {
      setBaseRegisterLoading(false);
    }
  };

  const fetchBaseTenants = async () => {
    setBaseLoading(true);
    try {
      const response = await api.get('/tenants', {
        params: { page: 1, limit: 50, is_base: true, status: 'all' }
      });
      setBaseTenants(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load base tenants');
    } finally {
      setBaseLoading(false);
    }
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: pageSize,
        search,
        status: status !== 'all' ? status : undefined
      };
      // List layout always loads provisioned into this slot; tab "all" loads mixed.
      if (listLayout === 'list' || tenantSegment === 'provisioned') {
        params.is_base = false;
      } else if (tenantSegment === 'base') {
        params.is_base = true;
      }
      // tenantSegment === 'all' → omit is_base
      const response = await api.get('/tenants', { params });
      setTenants(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async (tenantId, showLoading = true) => {
    if (showLoading) setProgressLoading(true);
    try {
      const response = await api.get(`/provisioning/${tenantId}/progress`);
      setProgressData(response.data.data);
      
      if (response.data.data.provisioning_status !== progressTenant?.provisioning_status) {
        setProgressTenant(prev => ({
          ...prev,
          provisioning_status: response.data.data.provisioning_status
        }));
        if (response.data.data.provisioning_status === 'completed' || 
            response.data.data.provisioning_status === 'failed') {
          fetchTenants();
        }
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    } finally {
      if (showLoading) setProgressLoading(false);
    }
  };

  const handleStatusClick = (tenant, event) => {
    event.stopPropagation();
    if (['in_progress', 'completed', 'failed', 'ready'].includes(tenant.provisioning_status)) {
      setProgressTenant(tenant);
      setProgressData(null);
      setProgressDialog(true);
      fetchProgress(tenant.id);
    }
  };

  const handleMenuOpen = (event, tenant) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenant(tenant);
  };

  const handleMenuClose = (clearSelected = true) => {
    setAnchorEl(null);
    if (clearSelected) {
      setSelectedTenant(null);
    }
  };

  // Workflow Actions
  const handleVerify = async () => {
    try {
      await api.post(`/tenants/${selectedTenant.id}/verify`);
      toast.success('Tenant verified');
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to verify tenant');
    }
    handleMenuClose();
  };

  const handleApproveOnBehalfOfClient = async () => {
    if (!selectedTenant?.id) return;
    setClientBehalfLoading(true);
    try {
      await api.post(`/tenants/${selectedTenant.id}/approve-on-behalf-of-client`, {
        reason: clientBehalfReason.trim() || undefined
      });
      toast.success('Approved on behalf of client. You can now verify.');
      setClientBehalfDialog(false);
      setClientBehalfReason('');
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve on behalf of client');
    } finally {
      setClientBehalfLoading(false);
    }
  };

  const openVerificationDetails = async (tenant) => {
    try {
      const response = await api.get(`/tenants/${tenant.id}/verification-details`);
      setVerificationDetails(response.data.data);
      setSelectedTenant(tenant);
      setVerifyDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load verification details');
    }
  };

  const openSendForApproval = async () => {
    try {
      const response = await api.get('/tenants/managers');
      setManagers(response.data.data || []);
      setSelectedManagerId('');
      setSendForApprovalDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load managers');
    }
  };

  const handleSendForApproval = async () => {
    if (!selectedTenant?.id) return;
    try {
      await api.post(`/tenants/${selectedTenant.id}/send-for-approval`, {
        manager_id: selectedManagerId || undefined
      });
      toast.success('Tenant sent for approval. A Manager or Super Admin can now approve.');
      setSendForApprovalDialog(false);
      setSelectedManagerId('');
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send for approval');
    }
    handleMenuClose();
  };

  const handleApprove = async () => {
    try {
      await api.post(`/tenants/${selectedTenant.id}/approve`);
      toast.success('Tenant approved');
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve tenant');
    }
    handleMenuClose();
  };

  const handleReject = async () => {
    try {
      await api.post(`/tenants/${selectedTenant.id}/reject`, { reason: rejectReason });
      toast.success('Tenant rejected');
      fetchTenants();
      setRejectDialog(false);
      setRejectReason('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject tenant');
    }
    handleMenuClose();
  };

  const handleStartProvisioning = () => {
    navigate(`/tenants/${selectedTenant.id}?provision=start`);
    handleMenuClose();
  };

  const handleActivate = async () => {
    try {
      await api.post(`/tenants/${selectedTenant.id}/activate`);
      toast.success('Tenant activated');
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate tenant');
    }
    handleMenuClose();
  };

  const handleSuspend = async () => {
    try {
      await api.post(`/tenants/${selectedTenant.id}/suspend`, { reason: suspendReason });
      toast.success('Tenant suspended');
      fetchTenants();
      setSuspendDialog(false);
      setSuspendReason('');
    } catch (err) {
      toast.error('Failed to suspend tenant');
    }
    handleMenuClose();
  };

  const handleHealthCheck = async () => {
    try {
      const response = await api.post(`/tenants/${selectedTenant.id}/health-check`);
      const { health_status } = response.data.data;
      toast.success(`Health check: ${health_status}`);
      fetchTenants();
    } catch (err) {
      toast.error('Failed to check health');
    }
    handleMenuClose();
  };

  const isSuperAdmin = user?.role === 'super_admin';

  const openDeleteDialog = async (tenantOverride = null) => {
    const tenant = tenantOverride || selectedTenant;
    if (tenantOverride) setSelectedTenant(tenantOverride);
    if (isBaseTenant(tenant)) {
      showBaseProtected(tenant);
      return;
    }
    setDestroyPreview(null);
    setDeleteConfirmName('');
    setDeleteDestroyInfra(true);
    setDeleteBackup(true);
    setDeleteDialog(true);
    if (user?.role === 'super_admin' && tenant?.id) {      setDestroyPreviewLoading(true);
      try {
        const response = await api.get(`/tenants/${tenant.id}/destroy-preview`);
        const preview = response.data?.data;
        setDestroyPreview(preview);
        setDeleteBackup(Boolean(preview?.backup_recommended));
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load destroy preview');
      } finally {
        setDestroyPreviewLoading(false);
      }
    }
  };

  const formatEnvironment = (row) => {
    if (!row) return '—';
    if (row.environment_short_name) return row.environment_short_name;
    if (row.environment === 'custom' && row.custom_environment_name) return row.custom_environment_name;
    const match = TENANT_ENVIRONMENTS.find((e) => e.value === row.environment);
    return match?.shortName || row.environment || '—';
  };

  const openApprovalAction = async (tenant, kind) => {
    setSelectedTenant(tenant);
    if (kind === 'client') {
      if (user?.role === 'super_admin' && tenant.status === 'submitted') {
        setClientBehalfDialog(true);
      } else {
        openVerificationDetails(tenant);
      }
      return;
    }
    if (kind === 'verify') {
      openVerificationDetails(tenant);
      return;
    }
    if (kind === 'approve') {
      if (tenant.status !== 'erp_verified') {
        openVerificationDetails(tenant);
        return;
      }
      try {
        await api.post(`/tenants/${tenant.id}/approve`);
        toast.success('Tenant approved');
        fetchTenants();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to approve');
        // Fall back to send-for-approval window if direct approve is blocked (e.g. SoD)
        openSendForApproval();
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedTenant?.id) return;
    if (isSuperAdmin) {
      if (deleteConfirmName.trim() !== selectedTenant.tenant_name) {
        toast.error('Type the tenant name exactly to confirm destroy');
        return;
      }
    }
    setDeleteLoading(true);
    try {
      const params = {};
      if (isSuperAdmin) {
        params.force = true;
        params.backup = deleteBackup;
        params.destroy = deleteDestroyInfra;
      }
      const response = await api.delete(`/tenants/${selectedTenant.id}`, { params });
      const data = response.data?.data || {};
      setDeleteDialog(false);
      setDestroyPreview(null);
      setDeleteConfirmName('');
      toast.success(response.data?.message || 'Delete/Destroy started');
      fetchTenants();
      if (data.status === 'destroying' || data.mode === 'async_destroy') {
        setDestroyProgressTenant(selectedTenant);
        setDestroyProgressOpen(true);
      } else if (data.infrastructure || data.mode === 'marked_deleted') {
        setDestroyReport(data);
        setDestroyReportOpen(true);
      }
    } catch (err) {
      const body = err.response?.data || {};
      if (body.data?.infrastructure || body.data?.steps) {
        setDestroyReport(body.data);
        setDestroyReportOpen(true);
      }
      toast.error(body.message || 'Failed to delete tenant');
    } finally {
      setDeleteLoading(false);
    }
    handleMenuClose();
  };

  const openDestroyProgress = async (tenant) => {
    setDestroyProgressTenant(tenant);
    setDestroyProgressOpen(true);
    try {
      const res = await api.get(`/tenants/${tenant.id}/destroy-progress`);
      setDestroyProgressData(res.data?.data || null);
    } catch {
      setDestroyProgressData({
        status: tenant.status,
        progress: tenant.destroy_progress,
        destroy_error: tenant.destroy_error
      });
    }
  };

  const handleRollbackDestroy = async () => {
    if (!selectedTenant?.id) return;
    setRollbackLoading(true);
    try {
      const response = await api.post(`/tenants/${selectedTenant.id}/rollback-destroy`);
      toast.success(response.data?.message || 'Tenant rolled back from backup');
      const errors = response.data?.data?.restore?.errors || [];
      errors.forEach((e) => toast.warn(e));
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to rollback tenant');
    } finally {
      setRollbackLoading(false);
      handleMenuClose();
    }
  };

  const requiresClientDeleteApproval = (tenant) => {
    if (!tenant) return false;
    if (user?.role === 'super_admin') return false;
    return ['client_approved', 'erp_verified', 'approved', 'in_progress', 'active', 'suspended'].includes(tenant.status);
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'running':
        return <CircularProgress size={18} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'warning':
        return <ErrorIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
      default:
        return <PendingIcon sx={{ color: 'grey.400', fontSize: 20 }} />;
    }
  };

  const getStatusChip = (row) => {
    if (row.status === 'destroying') {
      return (
        <Chip
          label="Delete/Destroy in Progress"
          color="warning"
          size="small"
          icon={<CircularProgress size={14} color="inherit" />}
          onClick={(e) => { e.stopPropagation(); openDestroyProgress(row); }}
          sx={{ fontWeight: 700, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
        />
      );
    }
    if (row.status === 'deleted' || row.deleted_at) {
      return (
        <Chip
          label="Deleted"
          color="default"
          size="small"
          variant="outlined"
          icon={<DeleteIcon />}
          onClick={(e) => { e.stopPropagation(); openDestroyProgress(row); }}
          sx={{ fontWeight: 700, cursor: 'pointer' }}
        />
      );
    }
    if (row.status === 'destroy_failed') {
      return (
        <Chip
          label="Destroy Failed"
          color="error"
          size="small"
          icon={<ErrorIcon />}
          onClick={(e) => { e.stopPropagation(); openDestroyProgress(row); }}
          sx={{ fontWeight: 700, cursor: 'pointer' }}
        />
      );
    }
    const isActive = row.status === 'active';
    return (
      <Chip
        label={isActive ? 'Active' : (row.status === 'suspended' ? 'Suspended' : String(row.status || 'inactive').replace(/_/g, ' '))}
        size="small"
        icon={isActive ? <CheckIcon /> : <ErrorIcon />}
        sx={{
          fontWeight: 700,
          textTransform: 'capitalize',
          color: '#fff',
          bgcolor: isActive ? '#2e7d32' : '#c62828',
          '& .MuiChip-icon': { color: '#fff' }
        }}
      />
    );
  };

  const getProvisioningStatusChip = (row) => {
    const provStatus = row.provisioning_status || 'not_started';
    const isClickable = ['ready', 'in_progress', 'completed', 'failed'].includes(provStatus);
    
    const statusConfig = {
      not_started: { color: 'default', label: 'Not Started', icon: null },
      ready: { color: 'info', label: 'Ready to Start', icon: <ProvisionIcon /> },
      in_progress: { color: 'warning', label: 'In Progress', icon: <CircularProgress size={14} /> },
      completed: { color: 'success', label: 'Completed', icon: <CheckIcon /> },
      failed: { color: 'error', label: 'Failed', icon: <ErrorIcon /> }
    };
    
    const config = statusConfig[provStatus] || statusConfig.not_started;
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        onClick={isClickable ? (e) => handleStatusClick(row, e) : undefined}
        sx={isClickable ? { 
          cursor: 'pointer',
          '&:hover': { transform: 'scale(1.05)', boxShadow: 2 },
          transition: 'all 0.2s'
        } : {}}
      />
    );
  };

  const buildColumns = (mode = 'hub') => [
    {
      field: 'tenant_id',
      headerName: 'Tenant ID',
      width: 178,
      minWidth: 178,
      flex: 0,
      sortable: true,
      renderCell: (params) => (
        <Typography
          variant="body2"
          title={params.value || ''}
          onClick={(e) => {
            e.stopPropagation();
            if (!params.row?.deleted_at) openTenantDetail(params.row);
          }}
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: 0.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            lineHeight: 1.4,
            color: 'primary.main',
            cursor: params.row?.deleted_at ? 'default' : 'pointer',
            textDecoration: 'none',
            '&:hover': params.row?.deleted_at ? undefined : { textDecoration: 'underline' }
          }}
        >
          {params.value || '—'}
        </Typography>
      )
    },
    {
      field: 'tenant_name',
      headerName: 'Name',
      width: 170,
      minWidth: 140,
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%' }}>
          {mode === 'base' && (
            <Chip
              size="small"
              label="BASE"
              sx={{
                flexShrink: 0,
                height: 22,
                bgcolor: '#c62828',
                color: '#fff',
                fontWeight: 700,
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}
          <Typography variant="body2" noWrap title={params.value} sx={{ minWidth: 0 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'company_name',
      headerName: 'Company',
      width: 150,
      minWidth: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value || ''} sx={{ maxWidth: '100%' }}>
          {params.value || '—'}
        </Typography>
      )
    },
    {
      field: 'environment',
      headerName: 'Env',
      width: 88,
      minWidth: 80,
      flex: 0,
      renderCell: (params) => (
        <Chip
          size="small"
          label={formatEnvironment(params.row)}
          variant="outlined"
          color={mode === 'base' ? 'error' : 'info'}
          sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />
      )
    },
    {
      field: 'versions',
      headerName: 'Version',
      width: mode === 'base' ? 320 : 280,
      minWidth: mode === 'base' ? 300 : 260,
      flex: 0,
      sortable: false,
      renderCell: (params) => (
        <ServiceVersionCell row={params.row} mode={params.row?.is_base ? 'base' : mode} />
      )
    },
    {
      field: 'plan',
      headerName: 'Plan',
      width: 96,
      minWidth: 88,
      flex: 0,
      renderCell: (params) => (
        mode === 'base' ? (
          <Chip label="Base" size="small" sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontWeight: 600 }} />
        ) : (
          <Chip
            label={params.value}
            size="small"
            color={
              params.value === 'enterprise' ? 'secondary' :
              params.value === 'standard' ? 'primary' : 'default'
            }
          />
        )
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      minWidth: 120,
      flex: 0,
      renderCell: (params) => (
        (mode === 'base' || params.row?.is_base) ? (
          <Chip
            size="small"
            icon={<LockIcon />}
            label="Registered"
            sx={{
              bgcolor: '#c62828',
              color: '#fff',
              maxWidth: '100%',
              '& .MuiChip-icon': { color: '#fff' },
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
            }}
          />
        ) : getStatusChip(params.row)
      )
    },
    {
      field: 'provisioning_status',
      headerName: 'Provisioning',
      width: 130,
      minWidth: 120,
      flex: 0,
      renderCell: (params) => (
        (mode === 'base' || params.row?.is_base) ? (
          <Chip size="small" label="N/A" variant="outlined" color="error" />
        ) : getProvisioningStatusChip(params.row)
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: mode === 'base' ? 120 : 220,
      minWidth: mode === 'base' ? 100 : 200,
      flex: 0,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const deleted = isDeletedRow(row) || row.status === 'destroying';
        if (mode === 'base' || row?.is_base) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="View base tenant">
                <IconButton size="small" onClick={() => openTenantDetail(row)}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Base tenant — cannot be deleted">
                <IconButton size="small" color="error" onClick={() => showBaseProtected(row)}>
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                  <MoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title={isDestroyLifecycle(row) ? 'View destroy progress' : 'View tenant'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => (isDestroyLifecycle(row) ? openDestroyProgress(row) : openTenantDetail(row))}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {can('tenants', 'edit') && (
              <Tooltip title="Edit (new window)">
                <span>
                  <IconButton
                    size="small"
                    disabled={deleted}
                    onClick={() => openPopup(`/tenants/${row.id}/edit`)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {can('tenants', 'delete') && !deleted && row.provisioning_status !== 'in_progress' && row.status !== 'destroy_failed' && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => openDeleteDialog(row)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {can('tenants', 'delete') && row.status === 'destroy_failed' && (
              <Tooltip title="Retry Delete & Destroy">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => openDeleteDialog(row)}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!deleted && (
              <>
                <Tooltip
                  title={
                    row.status === 'submitted'
                      ? (isSuperAdmin ? 'Client approval (Super Admin can approve on behalf)' : 'Awaiting client approval — view details')
                      : row.client_approved_at
                        ? `Client approved${row.client_approved_by_email ? `: ${row.client_approved_by_email}` : ''}`
                        : 'Client approval'
                  }
                >
                  <IconButton
                    size="small"
                    color={row.status === 'submitted' ? 'warning' : (row.client_approved_at || ['client_approved', 'erp_verified', 'approved', 'in_progress', 'active'].includes(row.status) ? 'success' : 'default')}
                    onClick={() => openApprovalAction(row, 'client')}
                  >
                    <PersonIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    row.status === 'client_approved'
                      ? 'ERP verify approval'
                      : row.erp_verified_at
                        ? 'ERP verified'
                        : 'ERP verify'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={!['client_approved', 'erp_verified', 'approved', 'in_progress', 'active'].includes(row.status)}
                      color={row.status === 'client_approved' ? 'info' : (row.erp_verified_at ? 'success' : 'default')}
                      onClick={() => openApprovalAction(row, 'verify')}
                    >
                      <HowToRegIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    row.status === 'erp_verified'
                      ? 'Final manager / Super Admin approval'
                      : row.approved_at
                        ? 'Final approved'
                        : 'Final approval'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={!['erp_verified', 'approved', 'in_progress', 'active'].includes(row.status)}
                      color={row.status === 'erp_verified' ? 'success' : (row.approved_at ? 'success' : 'default')}
                      onClick={() => openApprovalAction(row, 'approve')}
                    >
                      <ApproveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Tooltip title="More actions">
              <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                <MoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  const baseColumns = buildColumns('base');
  const hubColumns = buildColumns('hub');
  const mixedColumns = buildColumns('hub');

  const syncListParams = ({ nextView = viewMode, nextStatus = status, nextLayout = listLayout, nextSegment = tenantSegment } = {}) => {
    const params = {};
    if (nextStatus !== 'all') params.status = nextStatus;
    if (nextView === 'tree') params.view = 'tree';
    if (nextLayout !== 'list') params.layout = nextLayout;
    if (nextLayout !== 'list' && nextSegment !== 'all') params.segment = nextSegment;
    setSearchParams(params);
  };

  const filteredBaseTenants = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return baseTenants;
    return baseTenants.filter((t) => {
      const blob = `${t.tenant_id || ''} ${t.tenant_name || ''} ${t.subdomain || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [baseTenants, search]);

  const renderBaseTable = () => (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        border: '2px solid #c62828',
        borderRadius: 2,
        flex: 1,
        minWidth: 0
      }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(198, 40, 40, 0.08)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#b71c1c', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon fontSize="small" /> Non-editable registered tenant — Base tenant
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Developed, bug-fixed, and rolled out to many tenants through TenantHub.
          These rows are inventory only (discovered from Kubernetes) — not provisioned here, and cannot be edited or deleted.
        </Typography>
      </Box>
      <Box sx={{ height: Math.max(220, 56 + (filteredBaseTenants.length || 1) * 148) }}>
        <ErpDataGrid
          rows={filteredBaseTenants}
          columns={baseColumns}
          loading={baseLoading}
          hideFooter={filteredBaseTenants.length <= 5}
          pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          getRowHeight={() => 148}
          columnHeaderHeight={44}
          getRowClassName={() => 'tenant-row-base'}
          disableRowSelectionOnClick
          onRowClick={(params, event) => {
            const tag = event?.target?.closest?.('button, a, .MuiChip-root, .MuiIconButton-root, .MuiCheckbox-root');
            if (tag) return;
            openTenantDetail(params.row);
          }}
          sx={gridSx('base')}
        />
      </Box>
      <TenantTableLegend mode="base" />
    </Paper>
  );

  const renderHubTable = ({ mode = 'provisioned' } = {}) => {
    const isMixed = mode === 'all';
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider', flex: 1, minWidth: 0 }}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isMixed ? 'All tenants' : 'Provisioned through TenantHub'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isMixed
              ? 'Base and provisioned tenants together. Base rows stay non-editable; provisioned rows compare versions against base.'
              : 'Editable, modifiable, and destroyable. Versions are compared against the matching base tenant (dev/prod).'}
          </Typography>
        </Box>
        <Box sx={{ height: 520 }}>
          <ErpDataGrid
            rows={tenants}
            columns={isMixed ? mixedColumns : hubColumns}
            loading={loading}
            rowCount={total}
            pageSizeOptions={[10, 25, 50]}
            paginationModel={{ page, pageSize }}
            paginationMode="server"
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            getRowHeight={(params) => (params?.model?.is_base ? 148 : 92)}
            columnHeaderHeight={44}
            getRowClassName={(params) => (params.row?.is_base ? 'tenant-row-base' : '')}
            disableRowSelectionOnClick
            onRowClick={(params, event) => {
              const tag = event?.target?.closest?.('button, a, .MuiChip-root, .MuiIconButton-root, .MuiCheckbox-root');
              if (tag) return;
              if (!params.row?.deleted_at) openTenantDetail(params.row);
            }}
            onRowDoubleClick={(params) => {
              if (!params.row?.deleted_at) openTenantDetail(params.row);
            }}
            sx={gridSx(isMixed ? 'hub' : 'hub')}
          />
        </Box>
        <TenantTableLegend mode={isMixed ? 'hub' : 'hub'} />
      </Paper>
    );
  };

  const renderSegmentContent = (segment) => {
    if (segment === 'base') return renderBaseTable();
    if (segment === 'all') return renderHubTable({ mode: 'all' });
    return renderHubTable({ mode: 'provisioned' });
  };

  const segmentTabs = (
    <Tabs
      orientation={listLayout === 'vertical' ? 'vertical' : 'horizontal'}
      value={tenantSegment}
      onChange={(_, next) => {
        if (!next) return;
        setTenantSegment(next);
        setPage(0);
        syncListParams({ nextSegment: next });
      }}
      variant={listLayout === 'vertical' ? 'standard' : 'scrollable'}
      scrollButtons="auto"
      sx={
        listLayout === 'vertical'
          ? {
              borderRight: 1,
              borderColor: 'divider',
              minWidth: 210,
              '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left', minHeight: 48 }
            }
          : { borderBottom: 1, borderColor: 'divider', mb: 2 }
      }
    >
      {TENANT_SEGMENTS.map((seg) => (
        <Tab
          key={seg.value}
          value={seg.value}
          icon={seg.icon}
          iconPosition="start"
          label={seg.label}
        />
      ))}
    </Tabs>
  );

  const gridSx = (mode) => ({
    width: '100%',
    border: 'none',
    '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      borderRight: '1px solid',
      borderColor: 'divider',
      py: 0.5,
      px: 1
    },
    '& .MuiDataGrid-columnHeader': {
      borderRight: '1px solid',
      borderColor: 'divider'
    },
    '& .MuiDataGrid-cell[data-field="versions"]': {
      alignItems: 'flex-start',
      py: 0.75
    },
    '& .MuiDataGrid-cellContent': {
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    },
    ...(mode === 'base' ? {
      '& .tenant-row-base': {
        bgcolor: 'rgba(198, 40, 40, 0.08)',
        outline: '2px solid #c62828',
        outlineOffset: '-2px',
        '&:hover': { bgcolor: 'rgba(198, 40, 40, 0.14)' }
      }
    } : {})
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Tenants
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, next) => {
              if (!next) return;
              setViewMode(next);
              syncListParams({ nextView: next });
            }}
          >
            <ToggleButton value="table">
              <TableIcon sx={{ mr: 0.75 }} fontSize="small" /> Table
            </ToggleButton>
            <ToggleButton value="tree">
              <TreeIcon sx={{ mr: 0.75 }} fontSize="small" /> Company tree
            </ToggleButton>
          </ToggleButtonGroup>
          {viewMode === 'table' && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={listLayout}
              onChange={(_, next) => {
                if (!next) return;
                setListLayout(next);
                setPage(0);
                syncListParams({ nextLayout: next });
              }}
              aria-label="Tenant list layout"
            >
              <ToggleButton value="list" aria-label="Stacked list">
                <ListLayoutIcon sx={{ mr: 0.5 }} fontSize="small" /> List
              </ToggleButton>
              <ToggleButton value="horizontal" aria-label="Horizontal tabs">
                <HorizontalTabsIcon sx={{ mr: 0.5 }} fontSize="small" /> H-Tabs
              </ToggleButton>
              <ToggleButton value="vertical" aria-label="Vertical tabs">
                <VerticalTabsIcon sx={{ mr: 0.5 }} fontSize="small" /> V-Tabs
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {can('tenants', 'add') && (
            <Button
              variant="outlined"
              color="error"
              startIcon={baseRegisterLoading ? <CircularProgress size={16} color="inherit" /> : <DnsIcon />}
              disabled={baseRegisterLoading}
              onClick={registerBaseTenants}
            >
              Register base tenants
            </Button>
          )}
          {can('tenants', 'add') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddTenantChooserOpen(true)}
              sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }}
            >
              Add Tenant
            </Button>
          )}
        </Box>
      </Box>

      <Dialog open={addTenantChooserOpen} onClose={() => setAddTenantChooserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tenant</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose how you want to create this tenant.
          </DialogContentText>
          <List>
            <ListItemButton
              onClick={() => {
                setAddTenantChooserOpen(false);
                navigate('/tenants/onboard');
              }}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
            >
              <ListItemIcon><TreeIcon color="primary" /></ListItemIcon>
              <ListItemText
                primary="Onboard Tenant (workflow)"
                secondary="Step through company → contacts → associate contacts → tenant details"
              />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                setAddTenantChooserOpen(false);
                navigate('/tenants/new');
              }}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <ListItemIcon><QuickAddIcon /></ListItemIcon>
              <ListItemText
                primary="Quick Add Tenant"
                secondary="Single form using an existing company and contact"
              />
            </ListItemButton>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTenantChooserOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Filters: search left, status icons right */}
      <Paper
        sx={{
          p: 1.5,
          mb: 3,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <TextField
            placeholder="Search tenants..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220, flex: '1 1 220px', maxWidth: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={() => { fetchBaseTenants(); fetchTenants(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 700, letterSpacing: 0.4 }}>
            STATUS
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={status}
            onChange={(_, next) => {
              if (next == null) return;
              setStatus(next);
              setPage(0);
              syncListParams({ nextStatus: next });
            }}
            sx={{
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                px: 0.85,
                py: 0.45,
                borderColor: 'divider'
              }
            }}
          >
            {STATUS_FILTER_OPTIONS.filter((opt) => !opt.superAdminOnly || isSuperAdmin).map((opt) => (
              <ToggleButton key={opt.value} value={opt.value} aria-label={opt.label}>
                <Tooltip title={opt.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: status === opt.value ? `${opt.color}.main` : 'text.secondary' }}>
                    {opt.icon}
                  </Box>
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {viewMode === 'tree' ? (
        <TenantsCompanyTree
          search={search}
          canEdit={can('tenants', 'edit')}
          canDelete={can('tenants', 'delete')}
          onDeleteTenant={(tenant) => openDeleteDialog(tenant)}
          onViewTenant={(tenant) => openTenantDetail(tenant)}
        />
      ) : listLayout === 'list' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {renderBaseTable()}
          {renderHubTable({ mode: 'provisioned' })}
        </Box>
      ) : listLayout === 'vertical' ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 560 }}>
          {segmentTabs}
          <Box sx={{ flex: 1, minWidth: 0 }}>{renderSegmentContent(tenantSegment)}</Box>
        </Box>
      ) : (
        <Box>
          {segmentTabs}
          {renderSegmentContent(tenantSegment)}
        </Box>
      )}

      <TenantViewDialog
        open={viewDialogOpen}
        tenantId={viewTenantId}
        onClose={closeTenantView}
      />

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {!selectedTenant?.deleted_at && (
          <MenuItem onClick={() => { handleMenuClose(); openTenantDetail(selectedTenant); }}>
            <ViewIcon sx={{ mr: 1 }} /> View
          </MenuItem>
        )}
        {can('tenants', 'edit') && !selectedTenant?.deleted_at && (
        <MenuItem onClick={() => { handleMenuClose(); openPopup(`/tenants/${selectedTenant?.id}/edit`); }}>
          <EditIcon sx={{ mr: 1 }} /> Edit (new window)
        </MenuItem>
        )}
        {!selectedTenant?.deleted_at && <Divider />}

        {/* Workflow Actions (edit permission) */}
        {can('tenants', 'edit') && !selectedTenant?.deleted_at && user?.role === 'super_admin' && selectedTenant?.status === 'submitted' && (
          <MenuItem onClick={() => { handleMenuClose(false); setClientBehalfDialog(true); }}>
            <ApproveIcon sx={{ mr: 1, color: 'warning.main' }} /> Approve on Behalf of Client
          </MenuItem>
        )}
        {can('tenants', 'edit') && !selectedTenant?.deleted_at && selectedTenant?.status === 'client_approved' && (
          <MenuItem onClick={() => { handleMenuClose(); openVerificationDetails(selectedTenant); }}>
            <VerifiedIcon sx={{ mr: 1, color: 'info.main' }} /> Verify
          </MenuItem>
        )}
        {selectedTenant && !selectedTenant.deleted_at && ['submitted', 'client_approved', 'erp_verified', 'approved', 'in_progress'].includes(selectedTenant.status) && (
          <MenuItem onClick={() => { handleMenuClose(); openVerificationDetails(selectedTenant); }}>
            <InfoIcon sx={{ mr: 1 }} /> View Approval Details
          </MenuItem>
        )}
        {can('tenants', 'edit') && !selectedTenant?.deleted_at && selectedTenant?.status === 'erp_verified' && (
          <>
            <MenuItem onClick={handleApprove}>
              <ApproveIcon sx={{ mr: 1, color: 'success.main' }} /> Approve (Manager / Super Admin)
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(false); openSendForApproval(); }}>
              <InfoIcon sx={{ mr: 1 }} /> Send for approval
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(false); setRejectDialog(true); }}>
              <ErrorIcon sx={{ mr: 1, color: 'error.main' }} /> Reject
            </MenuItem>
          </>
        )}
        {can('tenants', 'edit') && selectedTenant?.status === 'approved' && selectedTenant?.provisioning_status === 'ready' && (
          <MenuItem onClick={handleStartProvisioning}>
            <ProvisionIcon sx={{ mr: 1, color: 'primary.main' }} /> Start Provisioning
          </MenuItem>
        )}
        {can('tenants', 'edit') && selectedTenant?.provisioning_status === 'completed' && selectedTenant?.status !== 'active' && (
          <MenuItem onClick={handleActivate}>
            <StartIcon sx={{ mr: 1, color: 'success.main' }} /> Activate
          </MenuItem>
        )}
        {can('tenants', 'edit') && selectedTenant?.status === 'active' && (
          <MenuItem onClick={() => { handleMenuClose(false); setSuspendDialog(true); }}>
            <SuspendIcon sx={{ mr: 1, color: 'warning.main' }} /> Suspend
          </MenuItem>
        )}

        <Divider />
        {can('tenants', 'view') && selectedTenant?.provisioning_status === 'completed' && !selectedTenant?.deleted_at && (
          <MenuItem onClick={handleHealthCheck}>
            <HealthIcon sx={{ mr: 1 }} /> Health Check
          </MenuItem>
        )}
        {isSuperAdmin && (isDeletedRow(selectedTenant) || selectedTenant?.status === 'destroy_failed') && selectedTenant?.backup_file_path && (
          <MenuItem onClick={handleRollbackDestroy} disabled={rollbackLoading} sx={{ color: 'success.main' }}>
            <RefreshIcon sx={{ mr: 1 }} /> {rollbackLoading ? 'Restoring…' : 'Restore / Rollback from Backup'}
          </MenuItem>
        )}
        {can('tenants', 'view') && isDestroyLifecycle(selectedTenant) && (
          <MenuItem onClick={() => { handleMenuClose(false); openDestroyProgress(selectedTenant); }}>
            <InfoIcon sx={{ mr: 1 }} /> View Destroy Progress
          </MenuItem>
        )}
        {isBaseTenant(selectedTenant) && (
          <MenuItem onClick={() => { handleMenuClose(false); showBaseProtected(selectedTenant); }} sx={{ color: 'error.main' }}>
            <LockIcon sx={{ mr: 1 }} /> Base tenant — cannot delete
          </MenuItem>
        )}
        {can('tenants', 'delete') && !isBaseTenant(selectedTenant)
          && selectedTenant?.provisioning_status !== 'in_progress'
          && !isDeletedRow(selectedTenant) && selectedTenant?.status !== 'destroying' && (
          <MenuItem onClick={() => { handleMenuClose(false); openDeleteDialog(); }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />{' '}
            {selectedTenant?.status === 'destroy_failed'
              ? 'Retry Delete & Destroy'
              : isSuperAdmin
                ? 'Delete & Destroy'
                : requiresClientDeleteApproval(selectedTenant)
                  ? 'Request Deletion Approval'
                  : 'Delete'}
          </MenuItem>
        )}
      </Menu>

      <Dialog open={baseProtectedDialog} onClose={() => setBaseProtectedDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#c62828', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon /> Base tenant — cannot be deleted
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" variant="outlined" sx={{ mb: 2, borderWidth: 2 }}>
            <strong>{baseProtectedTenant?.tenant_name || 'This base tenant'}</strong> is a registered ERP4C
            reference ({baseProtectedTenant?.environment_short_name || baseProtectedTenant?.environment || 'dev/prod'}).
            It was <strong>not built or provisioned through TenantHub</strong> — only discovered/registered from
            Kubernetes (ingress, TLS, DNS, database, image versions).
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Base tenants are the version source of truth. Customer tenants should lag or match these versions;
            the base stack stays ahead as new erp4c-base images are released.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tenant ID: {baseProtectedTenant?.tenant_id || '—'}
            {baseProtectedTenant?.k8s_namespace ? ` · Namespace: ${baseProtectedTenant.k8s_namespace}` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBaseProtectedDialog(false)}>Close</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setBaseProtectedDialog(false);
              if (baseProtectedTenant) openTenantDetail(baseProtectedTenant);
            }}
          >
            View base tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog} onClose={() => setSuspendDialog(false)}>
        <DialogTitle>Suspend Tenant</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to suspend tenant "{selectedTenant?.tenant_name}"?
          </Typography>
          <TextField
            fullWidth
            label="Suspension Reason"
            multiline
            rows={3}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialog(false)}>Cancel</Button>
          <Button onClick={handleSuspend} color="warning" variant="contained">
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send for approval Dialog */}
      <Dialog open={sendForApprovalDialog} onClose={() => setSendForApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send for approval</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Tenant &quot;{selectedTenant?.tenant_name}&quot; is ERP verified.
            {user?.role === 'super_admin'
              ? ' As Super Admin you can approve even if you verified (for process checks).'
              : ' Only a Manager or Super Admin can perform final approval (and must be a different person than the verifier).'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optionally assign to a manager below. Otherwise, any manager/super_admin can approve from the tenant list.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Notify manager (optional)</InputLabel>
            <Select
              value={selectedManagerId}
              label="Notify manager (optional)"
              onChange={(e) => setSelectedManagerId(e.target.value)}
            >
              <MenuItem value="">— None —</MenuItem>
              {managers.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.email})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendForApprovalDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendForApproval}>Send for approval</Button>
        </DialogActions>
      </Dialog>

      {/* Approve on behalf of client (Super Admin) */}
      <Dialog open={clientBehalfDialog} onClose={() => !clientBehalfLoading && setClientBehalfDialog(false)}>
        <DialogTitle>Approve on Behalf of Client</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Record client approval for &quot;{selectedTenant?.tenant_name}&quot; as Super Admin when the client is unavailable.
            Status will become <strong>Client Approved</strong>.
          </Alert>
          <TextField
            fullWidth
            label="Reason (optional)"
            multiline
            rows={3}
            value={clientBehalfReason}
            onChange={(e) => setClientBehalfReason(e.target.value)}
            placeholder="e.g. Client unreachable; verbal approval received"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientBehalfDialog(false)} disabled={clientBehalfLoading}>Cancel</Button>
          <Button
            onClick={handleApproveOnBehalfOfClient}
            color="warning"
            variant="contained"
            disabled={clientBehalfLoading}
          >
            {clientBehalfLoading ? 'Approving…' : 'Approve on Behalf of Client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Tenant</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please provide a reason for rejecting tenant "{selectedTenant?.tenant_name}".
          </Typography>
          <TextField
            fullWidth
            required
            label="Rejection Reason"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={!rejectReason}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete / Destroy Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => !deleteLoading && setDeleteDialog(false)}
        maxWidth={isSuperAdmin ? 'md' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          {isSuperAdmin ? 'Delete & Destroy Tenant' : 'Delete Tenant'}
        </DialogTitle>
        <DialogContent>
          {isSuperAdmin ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Alert severity="error">
                Review the numbered plan below. Prefer <strong>Backup &amp; Destroy</strong> so you can restore later.
              </Alert>

              {destroyPreviewLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading destroy plan…</Typography>
                </Box>
              )}

              {destroyPreview && (
                <TenantDestroyPlanPanel preview={destroyPreview} backupEnabled={deleteBackup} />
              )}

              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={deleteBackup}
                      onChange={(e) => setDeleteBackup(e.target.checked)}
                      color="warning"
                    />
                  )}
                  label="Step 0 — Create backup before destroy (required for rollback)"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={deleteDestroyInfra}
                      onChange={(e) => setDeleteDestroyInfra(e.target.checked)}
                    />
                  )}
                  label="Run destroy steps 1–5 (containers, folder, DB, k8s, records)"
                />
              </FormGroup>

              <TextField
                fullWidth
                label={`Type "${selectedTenant?.tenant_name || ''}" to confirm`}
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                disabled={deleteLoading}
              />
            </Box>
          ) : requiresClientDeleteApproval(selectedTenant) ? (
            <Typography>
              Tenant &quot;{selectedTenant?.tenant_name}&quot; is already beyond submitted status.
              Deletion requires customer consent. Continuing will send a deletion approval request to the customer.
            </Typography>
          ) : (
            <Typography>
              Are you sure you want to delete tenant &quot;{selectedTenant?.tenant_name}&quot;? This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleteLoading}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={
              deleteLoading
              || destroyPreviewLoading
              || (isSuperAdmin && deleteConfirmName.trim() !== (selectedTenant?.tenant_name || ''))
            }
          >
            {deleteLoading
              ? 'Destroying…'
              : isSuperAdmin
                ? (deleteBackup ? 'Backup & Destroy' : 'Destroy Without Backup')
                : requiresClientDeleteApproval(selectedTenant)
                  ? 'Send Approval Request'
                  : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Provisioning Progress Dialog */}
      <Dialog 
        open={progressDialog} 
        onClose={() => setProgressDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { minHeight: 400 } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {progressTenant?.provisioning_status === 'in_progress' ? (
              <InProgressIcon color="warning" />
            ) : progressTenant?.provisioning_status === 'failed' ? (
              <ErrorIcon color="error" />
            ) : progressTenant?.provisioning_status === 'completed' ? (
              <CheckIcon color="success" />
            ) : (
              <ProvisionIcon color="info" />
            )}
            <Box>
              <Typography variant="h6">
                Provisioning: {progressTenant?.tenant_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progressTenant?.tenant_id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setProgressDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          {progressLoading && !progressData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : progressData ? (
            <Box>
              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Progress
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {progressData.progress_percent}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progressData.progress_percent}
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: progressData.provisioning_status === 'failed' 
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : progressData.provisioning_status === 'completed'
                        ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                    }
                  }}
                />
              </Box>

              {/* Status Alerts */}
              {progressData.current_step && progressData.provisioning_status === 'in_progress' && (
                <Alert severity="info" sx={{ mb: 3 }} icon={<CircularProgress size={20} />}>
                  <Typography variant="subtitle2">{progressData.current_step.step}</Typography>
                  {progressData.current_step.message && (
                    <Typography variant="body2">{progressData.current_step.message}</Typography>
                  )}
                </Alert>
              )}

              {progressData.error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">Provisioning Failed</Typography>
                  <Typography variant="body2">{progressData.error}</Typography>
                </Alert>
              )}

              {progressData.provisioning_status === 'completed' && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">Provisioning Completed Successfully!</Typography>
                  <Typography variant="body2">
                    The tenant infrastructure is ready. You can now activate the tenant.
                  </Typography>
                </Alert>
              )}

              {/* Infrastructure Details */}
              {progressData.infrastructure && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Infrastructure Details</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Folder</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {progressData.infrastructure.folder_path?.split('/').pop()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Database</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {progressData.infrastructure.database_name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Frontend URL</Typography>
                      <Typography variant="body2">{progressData.infrastructure.urls?.frontend}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Backend URL</Typography>
                      <Typography variant="body2">{progressData.infrastructure.urls?.backend}</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* Steps */}
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Provisioning Steps
              </Typography>
              <Stepper orientation="vertical" activeStep={progressData.current_step_index}>
                {progressData.steps?.map((step, index) => (
                  <Step key={index} completed={step.status === 'completed' || step.status === 'warning'}>
                    <StepLabel
                      icon={getStepIcon(step.status)}
                      error={step.status === 'failed'}
                      sx={{ '& .MuiStepLabel-label': { fontWeight: step.status === 'running' ? 600 : 400 } }}
                    >
                      <Typography variant="body2">{step.step}</Typography>
                    </StepLabel>
                    <StepContent>
                      {step.message && (
                        <Typography variant="body2" color="text.secondary">{step.message}</Typography>
                      )}
                      {step.error && <Alert severity="error" sx={{ mt: 1 }}>{step.error}</Alert>}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {/* Timestamps */}
              {(progressData.started_at || progressData.completed_at) && (
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    {progressData.started_at && `Started: ${new Date(progressData.started_at).toLocaleString()}`}
                    {progressData.completed_at && ` • Completed: ${new Date(progressData.completed_at).toLocaleString()}`}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              {progressTenant?.provisioning_status === 'ready' 
                ? 'Provisioning is ready to start. Use the menu to start provisioning.'
                : 'No provisioning data available.'}
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={() => setProgressDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={() => { setProgressDialog(false); navigate(`/tenants/${progressTenant?.id}`); }}
          >
            View Tenant Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Prompt Dialog */}
      <Dialog open={verifyDialog} onClose={() => setVerifyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Verification Details</DialogTitle>
        <DialogContent>
          {verificationDetails ? (
            <Box>
              <Typography variant="body2"><strong>Tenant:</strong> {verificationDetails.tenant?.tenant_name} ({verificationDetails.tenant?.tenant_id})</Typography>
              <Typography variant="body2"><strong>Version:</strong> {verificationDetails.source?.source_version || '-'}</Typography>
              <Typography variant="body2"><strong>Source:</strong> {verificationDetails.source?.source_location || '-'}</Typography>
              <Typography variant="body2"><strong>Destination:</strong> {verificationDetails.destination?.target_location || '-'}</Typography>
              <Typography variant="body2"><strong>Helm Chart:</strong> {verificationDetails.deployment_artifacts?.helm_chart || '-'}</Typography>
              <Typography variant="body2"><strong>Namespace:</strong> {verificationDetails.deployment_artifacts?.namespace || '-'}</Typography>
              <Typography variant="body2"><strong>Database:</strong> {verificationDetails.deployment_artifacts?.database_name || '-'}</Typography>
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)}>Close</Button>
          {selectedTenant?.status === 'client_approved' && (
            <Button
              variant="contained"
              color="info"
              onClick={async () => {
                await handleVerify();
                setVerifyDialog(false);
              }}
            >
              Verify Now
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <TenantDestroyReportDialog
        open={destroyReportOpen}
        report={destroyReport}
        title={(destroyReport?.infrastructure?.errors || []).length
          ? 'Destroy incomplete — review steps'
          : 'Destroy complete — review steps'}
        onClose={() => {
          setDestroyReportOpen(false);
          setDestroyReport(null);
          fetchTenants();
        }}
      />

      <TenantDestroyProgressDialog
        open={destroyProgressOpen}
        tenant={destroyProgressTenant}
        progressPayload={destroyProgressData}
        onClose={() => {
          setDestroyProgressOpen(false);
          setDestroyProgressTenant(null);
          setDestroyProgressData(null);
          fetchTenants();
        }}
      />
    </Box>
  );
};

export default TenantsList;
